import { Button, IconButton, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import net from "../../../data/network.json";
import {
  wrapperCoins,
  wrapperItem,
  wrapperMain,
  wrapperToken,
  wrapperTopPart,
} from "./wallet.style";
import CustomCard from "../../../components/card";
import { wrapperInput } from "../tokens/token.style";
import { FC, useEffect, useState } from "react";
import useAppDispatch from "../../../hooks/useDispatch";
import {
  setFromWalletAddressData,
  setToWalletAddressData,
  setValidAddressFromData,
  setValidAddressToData,
} from "../../../redux/slices/walletAddress";
import SwapConfirmation from "../swapConfirmation";
import useAppSelector from "../../../hooks/useSelector";
import EastIcon from "@mui/icons-material/East";
import { wrapperCex } from "../swapConfirmation/SwapConfirmation.style";
import partner from "../../../data/partner.json";
import {
  setExtraIdFromData,
  setExtraIdToData,
} from "../../../redux/slices/extraIdSlice";
import Timer from "../../../components/timer";
import { useMutation } from "@tanstack/react-query";
import { validateAddressService } from "../../../services/validAddress";
import { useRef } from "react";
import { debounce } from "lodash";
import { baseImageUrl } from "../../../config/imageUrl";
import { AxiosError } from "axios";

interface IProps {
  setShowWallet: (arg0: boolean) => void;
  swapDetail: boolean;
  setSwapDetail: (swapDetail: boolean) => void;
  confirm: boolean;
  setConfirm: (confirm: boolean) => void;
  historyRequestId: string;
  setHistoryRequestId: (historyRequestId: string) => void;
  setSwitch: (arg0: number) => void;
  setLoading: (arg0: boolean) => void;
  timer: any;
  loading: boolean;
  fromAddressError: string;
  setFromAddressError: (fromAddressError: string) => void;
  toAddressError: string;
  setToAddressError: (toAddressError: string) => void;
  setActive: (arg0: number) => void;
}

const Wallet: FC<IProps> = ({
  setShowWallet,
  swapDetail,
  setSwapDetail,
  confirm,
  setConfirm,
  historyRequestId,
  setHistoryRequestId,
  setSwitch,
  setLoading,
  timer,
  loading,
  fromAddressError,
  setFromAddressError,
  toAddressError,
  setToAddressError,
  setActive,
}) => {
  const { coinData } = useAppSelector((state) => state.coinSelect);
  const { netIconFrom } = useAppSelector((state) => state.netIcon);
  const { netIconTo } = useAppSelector((state) => state.netIcon);
  const [loadingButton, setLoadingButton] = useState(false);
  const dispatch = useAppDispatch();

  const toWalletAddressData = useAppSelector(
    (state) => state.walletAddress.toWalletAddress
  );

  const fromWalletAddressData = useAppSelector(
    (state) => state.walletAddress.fromWalletAddress
  );

  const validAddressToData = useAppSelector(
    (state) => state.walletAddress.validAddressTo
  );

  const validAddressFromData = useAppSelector(
    (state) => state.walletAddress.validAddressFrom
  );

  const fromExtraData = useAppSelector((state) => state.extraId.extraIdFrom);
  const toExtraData = useAppSelector((state) => state.extraId.extraIdTo);

  const fromValueData = useAppSelector(
    (state) => state.coinFromValue.fromValue
  );
  const selectedExchangeData = useAppSelector(
    (state) => state.selectedExchange
  );

  const exchangeResData = useAppSelector((state) => state.exchangeRes);

  const netMemoFromData = useAppSelector((state) => state.netMemo.netMemoFrom);
  const netMemoToData = useAppSelector((state) => state.netMemo.netMemoTo);

  const handleBack = () => {
    setShowWallet(false);
  };

  const validateAddressQuery = useMutation({
    mutationFn: validateAddressService,
  });

  const prevAddress = useRef("");

  const isChangellyFixed =
    (selectedExchangeData?.selectedExchange?.partner_name === "Changelly" ||
      exchangeResData?.exchangeRes?.data[0]?.partner_name === "Changelly") &&
    (selectedExchangeData?.selectedExchange?.exchange_type === "fixed" ||
      exchangeResData?.exchangeRes?.data[0]?.exchange_type === "fixed");

  const handleToWalletAddress = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const rawValue = e.target.value;
    const trimmedValue = rawValue.trim();
    const valueLower = trimmedValue.toLowerCase();

    dispatch(setToWalletAddressData(trimmedValue));
    if (!trimmedValue) {
      dispatch(setValidAddressToData(false));
      setLoadingButton(false);
      setToAddressError("");
      prevAddress.current = "";
      return;
    }

    const selectedNetwork = net.find(
      (item) => item?.name.toUpperCase() === coinData[1]?.network.toUpperCase()
    )?.name;

    if (!selectedNetwork) {
      setLoadingButton(false);
      dispatch(setValidAddressToData(false));
      return;
    }

    const param = {
      address: trimmedValue,
      network: selectedNetwork,
    };

    const prevValueLower = prevAddress.current.toLowerCase();
    const isInputChanged = valueLower !== prevValueLower;

    if (isInputChanged) {
      setLoadingButton(true);
      dispatch(setValidAddressToData(false));
      debouncedValidate(param);
    }

    prevAddress.current = trimmedValue;
  };

  const debouncedValidate = useRef(
    debounce((param) => {
      validateAddressQuery.mutate(param, {
        onSuccess(data) {
          setLoadingButton(false);
          if (data?.data?.message === "OK" && data?.data?.valid) {
            dispatch(setValidAddressToData(true));
            setToAddressError("");
          } else if (data?.data?.message === "OK" && !data?.data?.valid) {
            dispatch(setValidAddressToData(false));
            setToAddressError(
              "Invalid recipient address. Please check and try again."
            );
          } else if (data?.data?.message !== "OK") {
            dispatch(setValidAddressToData(true));
          }
        },
        onError(error) {
          const axiosError = error as AxiosError;
          if (axiosError.response?.status === 403) {
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          }
          if (toWalletAddressData.length <= 1) {
            dispatch(setValidAddressToData(false));
            setToAddressError(
              "Invalid recipient address. Please check and try again."
            );
          } else {
            setLoadingButton(false);
            dispatch(setValidAddressToData(true));
            setToAddressError("");
          }
        },
      });
    }, 1000)
  ).current;

  useEffect(() => {
    return () => {
      debouncedValidate.cancel?.();
    };
  }, []);

  const prevFromAddress = useRef("");

  const handleFromWalletAddress = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const rawValue = e.target.value;
    const trimmedValue = rawValue.trim();
    const valueLower = trimmedValue.toLowerCase();

    dispatch(setFromWalletAddressData(trimmedValue));

    if (!trimmedValue) {
      dispatch(setValidAddressFromData(false));
      setLoadingButton(false);
      setFromAddressError("");
      prevFromAddress.current = "";
      return;
    }

    const selectedNetwork = net.find(
      (item) => item?.name.toUpperCase() === coinData[0]?.network.toUpperCase()
    )?.name;

    if (!selectedNetwork) {
      setLoadingButton(false);
      dispatch(setValidAddressFromData(false));
      return;
    }

    const param = {
      address: trimmedValue,
      network: selectedNetwork,
    };

    const prevValueLower = prevFromAddress.current;
    const isInputChanged = valueLower !== prevValueLower;

    if (isInputChanged) {
      setLoadingButton(true);
      dispatch(setValidAddressFromData(false));
      debouncedFromValidate(param);
    }

    prevFromAddress.current = valueLower;
  };

  const debouncedFromValidate = useRef(
    debounce((param) => {
      validateAddressQuery.mutate(param, {
        onSuccess(data) {
          setLoadingButton(false);
          if (data?.data?.message === "OK" && data?.data?.valid) {
            dispatch(setValidAddressFromData(true));
            setFromAddressError("");
          } else if (data?.data?.message === "OK" && !data?.data?.valid) {
            dispatch(setValidAddressFromData(false));
            setFromAddressError(
              "Invalid refund address. Please check and try again."
            );
          } else if (data?.data?.message !== "OK") {
            dispatch(setValidAddressFromData(true));
          }
        },
        onError(error) {
          const axiosError = error as AxiosError;
          if (axiosError.response?.status === 403) {
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          }
          if (fromWalletAddressData.length <= 1) {
            dispatch(setValidAddressFromData(false));
            setFromAddressError(
              "Invalid refund address. Please check and try again."
            );
          } else {
            setLoadingButton(false);
            dispatch(setValidAddressFromData(true));
            setFromAddressError("");
          }
        },
      });
    }, 500)
  ).current;

  useEffect(() => {
    return () => {
      debouncedFromValidate.cancel?.();
    };
  }, []);

  const handleFromExtraId = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    dispatch(setExtraIdFromData(e.target.value));
  };

  const handleToExtraId = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    dispatch(setExtraIdToData(e.target.value));
  };

  const handleConfirm = () => {
    setConfirm(true);
  };

  return (
    <>
      {!confirm ? (
        <Grid
          sx={wrapperMain}
          className="animate__animated animate__fadeIn fix_layout"
        >
          <CustomCard>
            <Grid sx={wrapperToken} mb={7}>
              <Grid container>
                <Timer timer={timer} loading={loading} />
                <IconButton className={loading ? "loading" : ""}>
                  <img
                    src="./assets/images/routes/refresh.svg"
                    alt="flashift"
                  />
                </IconButton>
              </Grid>
              <IconButton onClick={handleBack}>
                <ArrowBackIosIcon />
              </IconButton>
            </Grid>
            <Grid
              sx={{
                ...wrapperCoins,
                background: (theme) => theme?.palette?.secondary?.light,
                borderRadius: "8px",
              }}
              p={2}
              mb={2}
              container
            >
              <Grid sx={wrapperItem}>
                <Typography pl={6}>From</Typography>
                <Grid container style={{ position: "relative" }}>
                  <img
                    src={
                      coinData[0]?.image
                        ? coinData[0].image.startsWith("http")
                          ? coinData[0].image
                          : `${baseImageUrl}/${coinData[0].image}`
                        : "./assets/images/coins/noPic.svg"
                    }
                    alt="flashift"
                    width="40px"
                    height="40px"
                  />
                  <img
                    alt="flashift"
                    src={
                      netIconFrom
                        ? netIconFrom
                        : `${baseImageUrl}/${
                            net.filter(
                              (item) =>
                                item?.name.toUpperCase() ===
                                coinData[0]?.network.toUpperCase()
                            )?.[0]?.image
                          }`
                    }
                    width="15.5px"
                    height="15.5px"
                    className="netIcon"
                    style={{ left: "30px" }}
                  />
                  <Typography variant="h6" pl={1} mt={0.5}>
                    {coinData[0]?.symbol.toUpperCase()}
                    <Typography>
                      {" "}
                      {net
                        .filter(
                          (item) =>
                            item?.name.toUpperCase() ===
                            coinData[0]?.network.toUpperCase()
                        )?.[0]
                        ?.name.toUpperCase()}
                    </Typography>
                  </Typography>
                </Grid>
                <Grid
                  container
                  style={{ justifyContent: "space-between" }}
                  pl={0.7}
                >
                  <Typography variant="h5">{fromValueData}</Typography>
                </Grid>
              </Grid>
              <Grid sx={wrapperItem}>
                <Typography pl={9.5}>To</Typography>
                <Grid container style={{ position: "relative" }}>
                  <Grid mr={1} mt={2}>
                    <EastIcon />
                  </Grid>
                  <img
                    src={
                      coinData[1]?.image
                        ? coinData[1].image.startsWith("http")
                          ? coinData[1].image
                          : `${baseImageUrl}/${coinData[1].image}`
                        : "./assets/images/coins/noPic.svg"
                    }
                    alt="flashift"
                    width="40px"
                    height="40px"
                  />
                  <img
                    alt="flashift"
                    src={
                      netIconTo
                        ? netIconTo
                        : `${baseImageUrl}/${
                            net.filter(
                              (item) =>
                                item?.name.toUpperCase() ===
                                coinData[1]?.network.toUpperCase()
                            )?.[0]?.image
                          }`
                    }
                    width="15.5px"
                    height="15.5px"
                    className="netIcon"
                    style={{ left: "50px" }}
                  />
                  <Typography variant="h6" pl={1} mt={0.5}>
                    {coinData[1]?.symbol.toUpperCase()}
                    <Typography>
                      {" "}
                      {net
                        .filter(
                          (item) =>
                            item?.name.toUpperCase() ===
                            coinData[1]?.network.toUpperCase()
                        )?.[0]
                        ?.name.toUpperCase()}
                    </Typography>
                  </Typography>
                </Grid>

                <Grid
                  container
                  style={{ justifyContent: "space-between" }}
                  pl={4.7}
                >
                  <Typography variant="h5">
                    {selectedExchangeData?.selectedExchange?.amount.toFixed(5)}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid
              sx={{
                ...wrapperCex,
                background: (theme) => theme?.palette?.secondary?.light,
                borderRadius: "8px",
              }}
            >
              <Grid>
                <Typography>
                  <span>
                    {selectedExchangeData?.selectedExchange?.partner_name ? (
                      <img
                        src={`${baseImageUrl}/${
                          partner.filter(
                            (src) =>
                              src?.name ===
                              selectedExchangeData?.selectedExchange
                                ?.partner_name
                          )?.[0]?.image_small
                        }`}
                        alt="flashift"
                        height="24px"
                      />
                    ) : (
                      <img
                        src={`${baseImageUrl}/${
                          partner.filter(
                            (src) =>
                              src?.name ===
                              exchangeResData?.exchangeRes?.data[0]
                                ?.partner_name
                          )?.[0]?.image_small
                        }`}
                        alt="flashift"
                        height="24px"
                      />
                    )}
                  </span>
                  <span> Swap Via</span>
                  <span>
                    {selectedExchangeData?.selectedExchange?.partner_name
                      ? selectedExchangeData?.selectedExchange?.partner_name
                      : exchangeResData?.exchangeRes?.data[0]?.partner_name}
                  </span>
                </Typography>
              </Grid>
              <Grid
                sx={{
                  color: (theme) => theme?.palette?.primary?.main,
                  fontWeight: "700",
                }}
                pl={1}
              >
                Cex -
                <img
                  style={{
                    width: "17px",
                    position: "relative",
                    top: "3px",
                  }}
                  src={
                    (selectedExchangeData?.selectedExchange?.exchange_type
                      ? selectedExchangeData?.selectedExchange?.exchange_type
                      : exchangeResData?.exchangeRes?.data[0]
                          ?.exchange_type) === "floating"
                      ? "/assets/images/routes/float.svg"
                      : "/assets/images/routes/fixed.png"
                  }
                />
                {selectedExchangeData?.selectedExchange?.exchange_type
                  ? selectedExchangeData?.selectedExchange?.exchange_type
                  : exchangeResData?.exchangeRes?.data[0]?.exchange_type}
              </Grid>
            </Grid>
            <Grid sx={wrapperTopPart}>
              <Typography variant="h6" mb={1}>
                Enter your
                <span style={{ marginLeft: "5px" }}>
                  {coinData[1]?.symbol.toUpperCase()}
                </span>
                <span className="net_wrapper" style={{ margin: "0 5px" }}>
                  {net
                    .filter(
                      (item) =>
                        item?.name.toUpperCase() ===
                        coinData[1]?.network.toUpperCase()
                    )?.[0]
                    ?.name.toUpperCase()}
                </span>
                Address
              </Typography>
            </Grid>
            <Grid mt={3} mb={1} sx={wrapperInput}>
              <label>Wallet Address</label>
              <textarea
                placeholder="Enter your Recipient Address here"
                value={toWalletAddressData}
                onChange={handleToWalletAddress}
              />
              <Typography
                sx={{
                  color: (theme) => theme?.palette?.error?.main,
                }}
              >
                {toAddressError}
              </Typography>
            </Grid>
            {netMemoToData && (
              <Grid mt={3} mb={1} sx={wrapperInput}>
                <label>Extra ID</label>
                <textarea
                  placeholder="Enter your Recipient Extra ID (if needed)"
                  value={toExtraData}
                  onChange={handleToExtraId}
                />
              </Grid>
            )}

            <Grid sx={wrapperTopPart} mt={2}>
              <Typography variant="h6" mb={1}>
                Enter your
                <span style={{ marginLeft: "5px" }}>
                  {coinData[0]?.symbol.toUpperCase()}
                </span>
                <span className="net_wrapper" style={{ margin: "0 5px" }}>
                  {net
                    .filter(
                      (item) =>
                        item?.name.toUpperCase() ===
                        coinData[0]?.network.toUpperCase()
                    )?.[0]
                    ?.name.toUpperCase()}
                </span>
                Address
                {!isChangellyFixed ? " (Optional)" : ""}
              </Typography>
            </Grid>
            <Grid mt={3} mb={1} sx={wrapperInput}>
              <label>Wallet Address</label>
              <textarea
                placeholder={`Enter your Refund Address${
                  !isChangellyFixed ? " (Optional)" : ""
                }`}
                value={fromWalletAddressData}
                onChange={handleFromWalletAddress}
              />
              <Typography
                sx={{
                  color: (theme) => theme?.palette?.error?.main,
                }}
              >
                {fromAddressError}
              </Typography>
            </Grid>
            {netMemoFromData && (
              <Grid mt={3} mb={1} sx={wrapperInput}>
                <label>Extra ID</label>
                <textarea
                  placeholder="Enter your Refund Extra ID (if needed)"
                  value={fromExtraData}
                  onChange={handleFromExtraId}
                />
              </Grid>
            )}
            <Button
              variant="contained"
              fullWidth
              onClick={handleConfirm}
              style={{ margin: "10px auto" }}
              disabled={
                isChangellyFixed || fromWalletAddressData
                  ? !(
                      validAddressFromData &&
                      validAddressToData &&
                      fromWalletAddressData !== "" &&
                      toWalletAddressData !== ""
                    )
                  : !(validAddressToData && toWalletAddressData !== "")
              }
            >
              {loadingButton ? (
                <>
                  <img
                    className="loading"
                    src="/assets/images/loading.svg"
                    alt="flashift"
                  />
                  Cheking Address...
                </>
              ) : (
                "Continue"
              )}
            </Button>
          </CustomCard>
        </Grid>
      ) : (
        <SwapConfirmation
          setConfirm={setConfirm}
          setShowWallet={setShowWallet}
          swapDetail={swapDetail}
          setSwapDetail={setSwapDetail}
          historyRequestId={historyRequestId}
          setSwitch={setSwitch}
          setHistoryRequestId={setHistoryRequestId}
          setLoading={setLoading}
          timer={timer}
          loading={loading}
          setActive={setActive}
        />
      )}
    </>
  );
};

export default Wallet;
